import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

function Section({ title, hint, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6">
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{title}</div>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea = false }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      {textarea ? (
        <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" />
      ) : (
        <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" />
      )}
    </div>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/app-settings");
      setS(data);
    } catch (err) {
      console.error(err);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const set = (k, v) => setS((cur) => ({ ...cur, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/app-settings", s);
      setS(data);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div data-testid="settings-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Configuration</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Invoice Settings</h1>
          </div>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60" data-testid="save-settings">
            <FloppyDisk size={16} /> {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>
      <div className="p-8 space-y-6 max-w-5xl">
        <Section title="Company Profile">
          <Field label="Company Name" value={s.company_name} onChange={(v) => set("company_name", v)} />
          <Field label="Phone" value={s.company_phone} onChange={(v) => set("company_phone", v)} />
          <div className="md:col-span-2"><Field label="Address" value={s.company_address} onChange={(v) => set("company_address", v)} textarea /></div>
          <Field label="GSTIN" value={s.company_gstin} onChange={(v) => set("company_gstin", v)} />
          <Field label="PAN" value={s.company_pan} onChange={(v) => set("company_pan", v)} />
          <Field label="CIN" value={s.company_cin} onChange={(v) => set("company_cin", v)} />
          <Field label="FSSAI" value={s.company_fssai} onChange={(v) => set("company_fssai", v)} />
          <Field label="Invoice Prefix" value={s.invoice_prefix} onChange={(v) => set("invoice_prefix", v)} />
        </Section>

        <Section title="Bank Details" hint="Shown on the invoice for NEFT / cheque payments.">
          <Field label="Account Holder" value={s.bank_holder} onChange={(v) => set("bank_holder", v)} />
          <Field label="Bank Name" value={s.bank_name} onChange={(v) => set("bank_name", v)} />
          <Field label="Branch" value={s.bank_branch} onChange={(v) => set("bank_branch", v)} />
          <Field label="Account Number" value={s.bank_account} onChange={(v) => set("bank_account", v)} />
          <Field label="IFSC" value={s.bank_ifsc} onChange={(v) => set("bank_ifsc", v)} />
        </Section>

        <Section title="UPI QR Code" hint="Default UPI ID used to generate the Scan-to-Pay QR on invoices.">
          <Field label="UPI ID" value={s.upi_id} onChange={(v) => set("upi_id", v)} />
          <Field label="UPI Name (displayed)" value={s.upi_name} onChange={(v) => set("upi_name", v)} />
          <label className="md:col-span-2 flex items-center gap-2 cursor-pointer">
            <Checkbox checked={!!s.upi_qr_enabled} onCheckedChange={(v) => set("upi_qr_enabled", !!v)} data-testid="upi-default-toggle" />
            <span className="text-sm text-slate-700">Enable UPI QR by default on new invoices</span>
          </label>
        </Section>

        <Section title="Email (Resend)" hint="Add RESEND_API_KEY in backend .env to enable email sending. Update sender address here.">
          <Field label="Sender Email (verified in Resend)" value={s.sender_email} onChange={(v) => set("sender_email", v)} />
        </Section>
      </div>
    </div>
  );
}
